<template>
  <main>
    <article class="row left-align">
      <section class="col s12">
        <router-link
          to="/tickeradmin/listoptions"
          class="blue-grey-text text-darken-4 strong"
        >
          Security Master
        </router-link>
        >
        <span>Edit Option</span>
      </section>
    </article>
    <h4>
      Edit Option
    </h4>
    <article class="row">
      <section class="input-field col s4">
        <input type="number"
               placeholder="123456"
               class="validate"
               v-model="option.id"
               pattern="^[0-9]+$"
               disabled
        >
        <label class="active">Option ID</label>
      </section>
      <look-ahead id="usm-edit-option-instrument-lookahead"
                  name="Instrument"
                  size="s4"
                  :picks="instrumentPicks"
                  @searched="searchInstruments"
                  @picked="handleInstrumentPick($event)"
                  watermark="MSFT or Microsoft"
                  :disabled="!$auth.hasRoles('admin')"
      />
      <autocomplete
        v-if="exchanges.length"
        id="usm-edit-option-exchange-autocomplete"
        name="Exchange"
        size="s4"
        :picks="exchangePicks"
        @picked="payload.exchange_id = Number($event.slice(1, $event.indexOf(' ')))"
        :disabled="!$auth.hasRoles('admin')"
      />
    </article>
    <article class="row">
      <section class="input-field col s3 switch">
        <label class="active">Option Type</label>
        <p>
          <span class="radio">
            <label>
              <input type="radio" v-model="payload.is_put" :value="true">
              <span>Put</span>
            </label>
          </span>
          <span class="radio">
            <label>
              <input type="radio" v-model="payload.is_put" :value="false">
              <span>Call</span>
            </label>
          </span>
        </p>
      </section>
      <section class="input-field col s3">
        <input type="text"
               placeholder="3.14"
               class="validate"
               pattern="^[0-9]+\.?[0-9]*$"
               v-model="payload.strike_price"
               :disabled="!$auth.hasRoles('admin')"
        >
        <label class="active">Strike Price</label>
      </section>
      <section class="input-field col s3">
        <input id="ticker-admin-add-option-expiration-date"
               type="text"
               class="datepicker validate"
               pattern="^[0-9]{4}-[0-9]{2}-[0-9]{2}$"
               v-model="payload.expiration_date"
               @change="payload.expiration_date = $event.target.value"
               :disabled="!$auth.hasRoles('admin')"
        >
        <label class="active">Expiration Date</label>
      </section>
      <section class="input-field col s3">
        <input type="number"
               placeholder="123"
               class="validate"
               pattern="^[0-9]*$"
               v-model="payload.apx_security_id"
               :disabled="!$auth.hasRoles('admin')"
        >
        <label class="active">
          APX Security ID
        </label>
        <span class="helper-text left grey-text text-lighten-2">Optional</span>
      </section>
    </article>
    <article class="row">
      <section class="input-field col s4">
        <input type="text"
                placeholder="MSFT111022C00060000"
                class="validate"
                v-model="payload.symbol"
                :disabled="!$auth.hasRoles('admin')"
        >
        <label class="active">OSI Symbol</label>
      </section>
      <section class="col s5" />
      <section class="col s3">
        <input class="btn waves-effect waves-light teal accent-4 right"
               type="submit"
               value="Submit"
               @click.prevent="saveOption"
               :disabled="!payload.underlying_instrument_parent_id || !payload.exchange_id || !Number(payload.strike_price) || !payload.expiration_date || payload.is_put === null"
        >
      </section>
    </article>
  </main>
</template>

<script>
import axios from 'axios';
import LookAhead from '@/components/LookAhead.vue';
import Autocomplete from '@/components/Autocomplete.vue';

const defaultPayload = {
  option_id: 0,
  underlying_instrument_parent_id: 0,
  exchange_id: 0,
  apx_security_id: null,
  strike_price: 0.0,
  is_put: null,
  expiration_date: '',
  symbol: '',
};

export default {
  name: 'TickerAdminEditOption',
  components: { LookAhead, Autocomplete },
  props: {},
  data() {
    return {
      exchanges: [],
      instruments: [],
      option: { underlying_instrument_parent: {}, exchange: {} },
      payload: { ...defaultPayload },
      instrumentSymbol: '',
    };
  },
  computed: {
    optionId() {
      return Number(this.$route.query.option_id);
    },
    instrumentPicks() {
      return this.instruments.map(instrument => `#${instrument.InstrumentId} ${instrument.Symbol} ${instrument.Name}`);
    },
    exchangePicks() {
      return this.exchanges.map(exchange => `#${exchange.id} ${exchange.symbol} ${exchange.name}`);
    },
    body() {
      const body = { option_id: this.payload.option_id };
      if (this.payload.underlying_instrument_parent_id !== this.option.underlying_instrument_parent.id) { body.underlying_instrument_parent_id = this.payload.underlying_instrument_parent_id; }
      if (this.payload.exchange_id !== this.option.exchange.id) { body.exchange_id = this.payload.exchange_id; }
      if (this.payload.is_put !== this.option.is_put) { body.is_put = this.payload.is_put; }
      if (this.payload.strike_price !== this.option.strike_price) { body.strike_price = Number(this.payload.strike_price); }
      if (this.payload.expiration_date !== this.option.expiration_date) { body.expiration_date = this.payload.expiration_date; }
      if (this.payload.apx_security_id !== this.option.apx_security_id) { body.apx_security_id = Number(this.payload.apx_security_id) || null; }
      if (this.payload.symbol !== this.option.symbol) { body.symbol = this.payload.symbol.trim(); }
      return body;
    },
    computedOsiSymbol() {
      function formatDate(dateString) {
        const date = new Date(dateString);
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset() + 1);
        return `${String(date.getFullYear() - 2000).padStart(2, '0')}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
      }
      function formatStrikePrice(strikePrice) {
        return String(strikePrice * 1000).padStart(8, '0');
      }
      if (this.instrumentSymbol && this.payload.expiration_date && this.payload.strike_price) {
        return `${this.instrumentSymbol}${formatDate(this.payload.expiration_date)}${this.payload.is_put ? 'P' : 'C'}${formatStrikePrice(this.payload.strike_price)}`;
      }
      return '';
    },
  },
  watch: {
    computedOsiSymbol() {
      this.payload.symbol = this.computedOsiSymbol;
    },
  },
  methods: {
    initSelect() {
      this.materialize.FormSelect.init(document.querySelectorAll('select'), {});
    },
    initDatepicker() {
      this.materialize.Datepicker.init(document.getElementById('ticker-admin-add-option-expiration-date'), {
        autoClose: false,
        format: 'yyyy-mm-dd',
        disableWeekends: true,
        showDaysInNextAndPreviousMonths: true,
        showClearBtn: true,
        onSelect() {},
      });
    },
    async fetchExchanges() {
      const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/exchanges/`);
      this.exchanges = data;
    },
    async searchInstruments(query) {
      if (query) {
        const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/quotes/instruments/search/?q=${query}`);
        this.instruments = data.SearchResults;
      }
    },
    async fetchOption() {
      const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/option/?option_id=${this.optionId}`);
      this.option = data;
      this.instrumentSymbol = this.option.underlying_instrument_parent.symbol;
      this.payload.option_id = this.option.id;
      this.payload.underlying_instrument_parent_id = this.option.underlying_instrument_parent.id;
      this.payload.exchange_id = this.option.exchange.id;
      this.payload.is_put = this.option.is_put;
      this.payload.strike_price = this.option.strike_price;
      this.payload.expiration_date = this.option.expiration_date;
      this.payload.apx_security_id = this.option.apx_security_id;
      if (this.option.underlying_instrument_parent.id) {
        document.getElementById('usm-edit-option-instrument-lookahead').value = `#${this.option.underlying_instrument_parent.id} ${this.option.underlying_instrument_parent.symbol} ${this.option.underlying_instrument_parent.name}`;
      }
      document.getElementById('usm-edit-option-exchange-autocomplete').value = `#${this.option.exchange.id} ${this.option.exchange.symbol} ${this.option.exchange.name}`;
    },
    async saveOption() {
      if (String(Object.keys(this.body)) === String(['option_id'])) {
        return this.toast('Please modify at least one field', 'pink');
      }
      try {
        await axios.put(`${this.$store.getters.VUE_APP_APING_URL}/insights/instruments/option/`, this.body);
        this.payload = { ...defaultPayload };
        await this.fetchOption();
        this.toast(`Saved!`, 'green');
      } catch (exception) {
        this.toast('Something went wrong. Please contact #investing-tech-help.', 'pink');
      }
    },
    handleInstrumentPick(event) {
      this.payload.underlying_instrument_parent_id = Number(event.slice(1, event.indexOf(' ')));
      this.instrumentSymbol = event.slice(event.indexOf(' ') + 1, event.indexOf(' ', event.indexOf(' ') + 1));
    },
  },
  async mounted() {
    await this.fetchExchanges();
    await this.fetchOption();
    await this.initDatepicker();
    await this.initSelect();
  },
};
</script>

<style lang="scss" scoped>
@import '@/main.scss';

.switch label input[type="checkbox"]:checked + .lever::after {
  background-color: #F1F1F1;
}

.radio {
  margin: 0px 5px 0px 5px;
}
</style>
